import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import toast, { Toaster } from "react-hot-toast";
import { useFormik } from "formik";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
  Button,
  Stack,
  InputAdornment,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { api } from "common";
import { useTranslation } from "react-i18next";

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiFilledInput-root": {
    backgroundColor: "transparent",
    "&:hover": { backgroundColor: "transparent" },
    "&.Mui-focused": { backgroundColor: "transparent" },
    "&:before": { borderBottom: "1px solid rgba(255, 255, 255, 0.7)" },
    "&:after": { borderBottom: "2px solid #fff" },
    "&:hover:not(.Mui-disabled):before": { borderBottom: "2px solid #fff" },
  },
  "& .MuiFilledInput-input": {
    padding: "8px 0",
    color: "#fff",
    fontSize: "1.1rem",
    backgroundColor: "transparent",
    "&:-webkit-autofill": {
      WebkitBoxShadow: "0 0 0 1000px transparent inset",
      WebkitTextFillColor: "#fff",
      caretColor: "#fff",
      transition: "background-color 5000s ease-in-out 0s",
    },
  },
  "& .MuiFormLabel-root": {
    color: "#fff",
    fontSize: "1rem",
    fontWeight: 500,
    transform: "none",
    position: "relative",
    marginBottom: "8px",
  },
  "& .MuiFormLabel-root.Mui-focused": { color: "#fff" },
}));

const SignInForm = ({ switchToSignUp }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const settings = useSelector((state) => state.settingsdata.settings);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const { t } = useTranslation();
  const { countries } = api;

  // Clear any previous auth errors when component mounts
  useEffect(() => {
    dispatch(api.clearLoginError());
  }, [dispatch]);

  useEffect(() => {
    if (auth.error && isLoading) {
      // Extract the error message from auth.error
      let errorMessage = "An error occurred during sign in";

      if (typeof auth.error === "string") {
        errorMessage = auth.error;
      } else if (auth.error.message) {
        errorMessage = auth.error.message;
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  }, [auth.error, isLoading]);

  useEffect(() => {
    if (auth.profile && !auth.loading) {
      if (
        auth.profile.usertype === "admin" ||
        auth.profile.usertype === "fleetadmin"
      ) {
        setError("Wrong Credentials. Please try again.");
        toast.error("Wrong Credentials. Please try again.", {
          duration: 5000,
          position: "top-center",
          // icon: "✅",
          // style: {
          //   background: "#e6ffed",
          //   color: "#0f5132",
          //   padding: "16px",
          //   borderRadius: "8px",
          //   fontWeight: "500",
          //   fontSize: "14px",
          //   boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          // },
        });
      } else if (
        auth.profile.usertype === "customer" ||
        auth.profile.usertype === "driver"
      ) {
        toast.success("You've Logged in successfully", {
          duration: 5000,
          position: "top-center",
          icon: "✅",
          style: {
            background: "#e6ffed",
            color: "#0f5132",
            padding: "16px",
            borderRadius: "8px",
            fontWeight: "500",
            fontSize: "14px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          },
        });
        setSuccess("Login successful");
      } else {
        setError("Wrong Credentials. Please try again.");
        toast.error("Wrong Credentials. Please try again.", {
          duration: 5000,
          position: "top-center",
        });
      }

      setIsLoading(false);
    }
  }, [auth.profile, auth.loading]);

  // Phone-based validation schema
  const validationSchema = Yup.object({
    phone: Yup.string()
      .required(t("phone_required") || "Phone number is required")
      .test("is-valid-phone", t("mobile_no_blank_error") || "Invalid phone number", function (value) {
        const { selectedcountry } = this.parent;
        if (!selectedcountry) return value && value.length >= 7;
        if (selectedcountry.label.toLowerCase() === "nigeria") {
          return /^[7-9][0-1][0-9]{8}$/.test(value);
        } else if (selectedcountry.label.toLowerCase() === "cameroon") {
          return /^[79][0-9]{7,8}$/.test(value);
        }
        return value && value.length >= 7;
      }),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .required("Password is required")
      .matches(/^\S*$/, "Password cannot contain spaces"),
  });

  // Get default country from settings
  const getDefaultCountry = () => {
    if (settings && countries) {
      const found = countries.find(c => c.label === settings.country);
      if (found) return found;
    }
    return countries && countries.length > 0 ? countries[0] : null;
  };

  const formik = useFormik({
    initialValues: {
      phone: localStorage.getItem("rememberedPhone") || "",
      selectedcountry: getDefaultCountry(),
      password: "",
    },
    validationSchema,
    enableReinitialize: true,

    onSubmit: async (values) => {
      setIsLoading(true);
      setError("");
      try {
        // Generate synthetic email from phone number for Firebase Auth
        const formattedPhone = values.phone.replace(/\s/g, "");
        const fullMobile = `+${values.selectedcountry.phone}${formattedPhone}`;
        const syntheticEmail = `${fullMobile.replace("+", "")}@haraka.phone`;

        console.log("SignInForm: Logging in with phone:", fullMobile);
        console.log("SignInForm: Using synthetic email:", syntheticEmail);
        dispatch(api.verifyEmailPassword(syntheticEmail, values.password));

        if (rememberMe) {
          localStorage.setItem("rememberedPhone", values.phone);
        } else {
          localStorage.removeItem("rememberedPhone");
        }
      } catch (err) {
        const errorMessage =
          err && typeof err.message === "string"
            ? err.message
            : "An error occurred during sign in";

        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    },
  });
  const handleForgotPassword = (e) => {
    e.preventDefault();
    navigate("/forgot-password");
  };

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{
          width: "100%",
          maxWidth: 480,
          zIndex: 2,
          p: 4,
          boxSizing: "border-box",
          borderRadius: 4,
          "@media (max-width: 768px)": {
            maxWidth: "100%",
          },
          "@media (min-width: 1024px)": {
            maxWidth: 540,
          },
        }}
      >
        <Typography
          variant="h3"
          align="center"
          sx={{ color: "#fff", mb: 2, fontWeight: 600 }}
        >
          {t(`SignIn`)}
        </Typography>
        <Typography align="center" sx={{ color: "rgb(255, 255, 255)", mb: 4 }}>
          {t(`NoAccount`)}?{" "}
          <Button
            onClick={switchToSignUp}
            sx={{
              color: "#9EC556",
              textTransform: "none",
              "&:hover": { color: "#AFCD56" },
            }}
          >
            {t(`signup`)}
          </Button>
        </Typography>
        {error && typeof error === "string" && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && typeof success === "string" && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        <Stack spacing={3}>
          {/* Phone Number with Country Code Dropdown */}
          <StyledTextField
            fullWidth
            label={t("phone") || "Phone Number"}
            name="phone"
            type="tel"
            value={formik.values.phone}
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/g, "");
              formik.setFieldValue("phone", value);
            }}
            onBlur={formik.handleBlur}
            placeholder="8012345678"
            variant="filled"
            autoComplete="tel"
            inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ height: "100%", maxHeight: "none", alignItems: "center" }}>
                  <Select
                    value={formik.values.selectedcountry?.phone || ""}
                    onChange={(e) => {
                      const country = countries.find(c => c.phone === e.target.value);
                      formik.setFieldValue("selectedcountry", country);
                    }}
                    variant="standard"
                    disableUnderline
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      minWidth: 70,
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      "& .MuiSelect-select": {
                        padding: "0 24px 0 0",
                        display: "flex",
                        alignItems: "center",
                      },
                      "& .MuiSelect-icon": {
                        color: "rgba(255, 255, 255, 0.7)",
                      },
                      "&:before, &:after": {
                        display: "none",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          maxHeight: 300,
                          bgcolor: "#333",
                          "& .MuiMenuItem-root": {
                            color: "#fff",
                          },
                        },
                      },
                    }}
                  >
                    {countries && countries.map((country) => (
                      <MenuItem key={country.code} value={country.phone}>
                        +{country.phone}
                      </MenuItem>
                    ))}
                  </Select>
                </InputAdornment>
              ),
            }}
          />
          <StyledTextField
            fullWidth
            label={t(`password`)}
            name="password"
            type={showPassword ? "text" : "password"}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            variant="filled"
            autoComplete="current-password"
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    sx={{ color: "rgba(255, 255, 255, 0.7)" }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    "&.Mui-checked": { color: "#6BB566" },
                  }}
                />
              }
              label={
                <Typography sx={{ color: "#fff" }}>
                  {t(`RememberMe`)}
                </Typography>
              }
            />
            <Button
              onClick={handleForgotPassword}
              sx={{
                textTransform: "capitalize",
                color: "rgba(255, 255, 255, 0.7)",
                maxWidth: 260,
                "&:hover": { color: "#fff" },
              }}
            >
              {t(`ForgotPassword`)}?
            </Button>
          </Box>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={!formik.isValid || !formik.dirty || isLoading}
            sx={{
              color: "#fff",
              py: 1.5,
              borderRadius: "50px",
              fontSize: "1.1rem",
              textTransform: "none",
              background:
                "linear-gradient(90deg, #59A959 0%, #79B357 65.87%, #AFCD56 95.67%)",
              "&:hover": { bgcolor: "#F8DBF2" },
              "&:disabled": { background: "rgba(255, 255, 255, 0.5)" },
              position: "relative",
            }}
          >
            {isLoading ? (
              <>
                <span style={{ visibility: "hidden" }}>{t("signIn")}</span>
                <CircularProgress
                  size={24}
                  sx={{
                    color: "white",
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              </>
            ) : (
              t("SignIn")
            )}
          </Button>
        </Stack>
      </Box>
    </>
  );
};
export default SignInForm;
